import { Component, Injector, OnInit, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { UserAccountService } from '../../../common/service/user-account.service';
import { CommandController } from '../../../core/component/base/command-controller.base';
import { ColumnDefinition, ColumnVisibility } from '../../../core/model/column-definition';
import { Command, CommandBase, DeleteCommand, standardCommand } from '../../../core/model/command';
import { MenuItemDefinition } from '../../../core/model/menu-item-definition';
import { ActionWindowService } from '../../../core/service/action-window.service';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { StandardCommandHandlerResolver } from '../../../core/service/command/standard-command-handler-resolver.service';
import { DeleteCommandHandler } from '../../../core/service/command/delete-command-handler.service';
import { DeleteCommandHandlerV2 } from '../../../core/service/command/delete-command-handler.v2.service';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { NativeWindowService } from '../../../core/service/native-window.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { leaseAgreementDocumentCommand } from '../../command/lease-agreement-document.command';
import { LeaseAgreementDocument } from '../../model/lease-agreement-document';
import { LeaseAgreementDocumentService } from '../../service/data/lease-agreement-document.service';
import { ChangeDocumentVisibilityComponent } from '../change-document-visibility/change-document-visibility.component';

@Component({
    selector: 'app-lease-agreement-document-manager',
    templateUrl: './lease-agreement-document-manager.component.html',
    styleUrls: ['./lease-agreement-document-manager.component.scss'],
    providers: [NavigationService, StandardCommandHandlerResolver, { provide: DeleteCommandHandler, useClass: DeleteCommandHandlerV2 }],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LeaseAgreementDocumentManagerComponent extends CommandController implements OnInit {
  public readonly contextId: string = 'cmd-' + Math.random().toString(36).slice(2);
  @Input() leaseAgreementId?: string;
  @Output() countChange = new EventEmitter<number>();
  constructor(
    private _activatedRoute: ActivatedRoute,
    private _dataService: LeaseAgreementDocumentService,
    private _nativeWindow: NativeWindowService,
    private _injector: Injector,
    private _dialogService: ActionWindowService,
    private _navigation: NavigationService,
    private _userAccount: UserAccountService,
    cdf: ColumnDefinitionFactory,
    commandHandlerResolver: StandardCommandHandlerResolver,
    dispatcher: CommandDispatcher
  ) {
    super(dispatcher, commandHandlerResolver);
    this.columnDefinitions = [
      cdf.create('LEASE_AGREEMENT_DOCUMENT.CAPTION', 'caption'),
      cdf.createDateTime('LEASE_AGREEMENT_DOCUMENT.UPLOADED_DATE', 'uploadedDate'),
      cdf.create('ESIGNATURE.STATUS', 'eSignatureStatusText'),
      cdf.create('COMMON.DISPLAYED_FOR', 'visibilityText'),
      cdf.create('LEASE_AGREEMENT_DOCUMENT.SIZE', 'size', ColumnVisibility.optional),
    ];
    this.contextMenuDefinitions = [
      new MenuItemDefinition('DOWNLOAD_BTN', standardCommand.download),
      new MenuItemDefinition('COMMON.CHANGE_DISPLAYED_FOR', leaseAgreementDocumentCommand.changeVisibilityLevel),
      new MenuItemDefinition('DELETE_BTN', standardCommand.delete),
    ];
  }

  columnDefinitions: ColumnDefinition[];
  contextMenuDefinitions: MenuItemDefinition[];
  items: LeaseAgreementDocument[];

  private changeVisibilitySub: Subscription;

  canExecute(command: CommandBase): boolean {
    const cmd = command as Command;
    if (cmd?.contextId && cmd.contextId !== this.contextId) {
      return false;
    }
    switch (command.id) {
      case standardCommand.delete:
      case standardCommand.download:
      case leaseAgreementDocumentCommand.createDigitalSigningErrand:
      case leaseAgreementDocumentCommand.changeVisibilityLevel:
        return command instanceof Command && !!command.context;

      default:
        return super.canExecute(command);
    }
  }

  execute(command: CommandBase): void {
    if (command instanceof Command) {
      switch (command.id) {
        case standardCommand.delete:
          const deleteCmd = command as DeleteCommand;
          deleteCmd.itemsSource = this.items;
          deleteCmd.dataService = this._dataService;

          super.execute(deleteCmd);
          break;

        case standardCommand.download:
          this.AddSubscription(
            this._dataService.download((<Command>command).context).subscribe(res => {
              this._nativeWindow.save(res);
            })
          );
          break;

        case leaseAgreementDocumentCommand.createDigitalSigningErrand:
          this.AddSubscription(this._dataService.requestESignature(command.context.id).subscribe());
          break;

        case leaseAgreementDocumentCommand.changeVisibilityLevel:
          if (this.changeVisibilitySub) {
            this.changeVisibilitySub.unsubscribe();
          }
          this.changeVisibilitySub = this._dialogService
            .open(ChangeDocumentVisibilityComponent, this._injector, null, { linkedLeaseAgreementDocuments: [command.context] })
            .subscribe(result => {
              Object.assign(command.context, result);
              this.changeVisibilitySub.unsubscribe();
            });
          break;
      }
    }
  }

  onRowClicked(e: { context: LeaseAgreementDocument; event: any }): void {
    this.AddSubscription(
      this._dataService.download(e.context).subscribe(res => {
        this._nativeWindow.save(res);
      })
    );
  }

  ngOnInit() {
    if (this.leaseAgreementId) {
      this.AddSubscription(
        this._dataService.getByLeaseAgreement(String(this.leaseAgreementId)).subscribe(res => {
          this.items = res;
          this.countChange.emit(this.items?.length ?? 0);
        })
      );
      return;
    }
    this.AddSubscription(
      this._activatedRoute.paramMap
        .pipe(
          switchMap(map => {
            let id = map.get('leaseAgreementId');
            if (id) {
              return this._dataService.getByLeaseAgreement(id);
            }
            return of([]);
          })
        )
        .subscribe(res => {
          this.items = res as LeaseAgreementDocument[];
          this.countChange.emit(this.items?.length ?? 0);
        })
    );
  }

  //Implemented to update the documents list after using delete command on document
  protected executedWithResult(result: boolean, command: CommandBase): void {
    if (result && command && command.id === standardCommand.delete) {
      const len = this.items?.length ?? 0;
      this.countChange.emit(len);
    }
  }

  public refresh(): void {
    if (this.leaseAgreementId) {
      this.AddSubscription(
        this._dataService.getByLeaseAgreement(String(this.leaseAgreementId)).subscribe(res => {
          setTimeout(() => {
            this.items = res;
            this.countChange.emit(this.items?.length ?? 0);
          }, 0);
        })
      );
      return;
    }
    this.AddSubscription(
      this._activatedRoute.paramMap
        .pipe(
          switchMap(map => {
            let id = map.get('leaseAgreementId');
            if (id) {
              return this._dataService.getByLeaseAgreement(id);
            }
            return of([]);
          })
        )
        .subscribe(res => {
          setTimeout(() => {
            this.items = res as LeaseAgreementDocument[];
            this.countChange.emit(this.items.length ?? 0);
          }, 0);
        })
    );
  }
}
